import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface BazaarCartItem {
  listingId: string;
  listingTitle: string;
  sellerId: string;
  sellerName: string;
  quantity: number;
  unitPrice: number;
  quoteId?: string;
  /** Buyer-chosen online-payment amount for this item. Unset until the buyer edits it in the basket — falls back to the platform minimum until then. */
  onlineAmount?: number;
}

interface BazaarCartState {
  items: BazaarCartItem[];
  addItem: (item: BazaarCartItem) => void;
  removeItem: (listingId: string) => void;
  updateQuantity: (listingId: string, quantity: number) => void;
  setItemOnlineAmount: (listingId: string, onlineAmount: number) => void;
  clearCart: () => void;
  totalItems: () => number;
  grandTotal: () => number;
  totalDeposit: (depositPercent: number) => number;
  totalCod: (depositPercent: number) => number;
}

// Persisted to localStorage — a basket that vanishes on refresh or a direct URL
// navigation (e.g. following a shared link, or the browser back/forward buttons)
// would be a real product bug, not just an in-memory convenience.
export const useBazaarCartStore = create<BazaarCartState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) =>
        set((s) => {
          const exists = s.items.find(i => i.listingId === item.listingId);
          if (exists) return s;
          return { items: [...s.items, item] };
        }),

      removeItem: (listingId) =>
        set((s) => ({ items: s.items.filter(i => i.listingId !== listingId) })),

      updateQuantity: (listingId, quantity) =>
        set((s) => ({
          // Changing quantity invalidates any online-amount the buyer already typed (it was sized
          // for the old line total) — clear it back to "use the platform minimum" rather than
          // silently keeping a now-stale (and possibly now-below-minimum) amount.
          items: s.items.map(i => i.listingId === listingId ? { ...i, quantity, onlineAmount: undefined } : i),
        })),

      setItemOnlineAmount: (listingId, onlineAmount) =>
        set((s) => ({
          items: s.items.map(i => i.listingId === listingId ? { ...i, onlineAmount } : i),
        })),

      clearCart: () => set({ items: [] }),

      totalItems: () => get().items.reduce((acc, i) => acc + i.quantity, 0),

      grandTotal: () => get().items.reduce((acc, i) => acc + i.unitPrice * i.quantity, 0),

      // depositPercent is the platform-minimum fallback for any item the buyer hasn't
      // individually edited yet — once BazaarBasketPage's per-item inputs are touched,
      // item.onlineAmount takes over for that line.
      totalDeposit: (depositPercent) =>
        Math.round(get().items.reduce((acc, i) =>
          acc + (i.onlineAmount ?? (i.unitPrice * i.quantity * (depositPercent / 100))), 0) * 100) / 100,

      totalCod: (depositPercent) => {
        const total = get().grandTotal();
        const deposit = get().totalDeposit(depositPercent);
        return Math.round((total - deposit) * 100) / 100;
      },
    }),
    { name: 'td-bazaar-cart', partialize: (s) => ({ items: s.items }) },
  ),
);
